import { useState } from "react";
import { Box, Text, Tooltip, ActionIcon, Collapse } from "@mantine/core";
import { ChevronUp, ChevronDown, Info } from "lucide-react";
import type { ReactNode } from "react";
import classes from "./Panel.module.css";

interface PanelProps {
  title: ReactNode;
  /** Optional hint shown under an ⓘ icon in the header. */
  info?: string;
  /** Content before the title (e.g. a drag handle). */
  headerLeft?: ReactNode;
  /** Extra header action on the right (e.g. a mode toggle), before the ⓘ/chevron. */
  headerRight?: ReactNode;
  /** Highlights the panel with a lighter frame (e.g. the selected item). */
  selected?: boolean;
  defaultCollapsed?: boolean;
  children: ReactNode;
}

/**
 * GitLab-style collapsible panel: a header strip (title on the left; an optional
 * action, an ⓘ hint, a divider and a collapse chevron on the right) over a
 * bordered, rounded body. The container's chrome — not a bare divider — is the
 * separator here.
 */
export function Panel({ title, info, headerLeft, headerRight, selected, defaultCollapsed = false, children }: PanelProps) {
  const [open, setOpen] = useState(!defaultCollapsed);
  return (
    <Box className={classes.panel} mb="md" data-selected={selected || undefined}>
      <div className={classes.header} data-open={open || undefined}>
        <div className={classes.left}>
          {headerLeft}
          <Text fw={600} c="var(--mantine-color-text)" style={{ minWidth: 0 }} truncate>{title}</Text>
        </div>
        <div className={classes.right}>
          {headerRight}
          {info && (
            <Tooltip label={info} withArrow multiline w={260} position="top-end">
              <Text c="dimmed" style={{ display: "flex", cursor: "help" }}><Info size={16} /></Text>
            </Tooltip>
          )}
          <span className={classes.divider} />
          <ActionIcon
            variant="subtle"
            color="gray"
            onClick={() => setOpen(o => !o)}
            aria-label={open ? "Свернуть" : "Развернуть"}
          >
            {open ? <ChevronUp size={18} /> : <ChevronDown size={18} />}
          </ActionIcon>
        </div>
      </div>
      <Collapse in={open}>
        <div className={classes.body}>{children}</div>
      </Collapse>
    </Box>
  );
}
